import { wmsApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

export interface DashboardSummary {
  pendingOrders: number;
  pickingOrders: number;
  packingOrders: number;
  shippedOrders: number;
  activeWaves: number;
  activePickers: number;
  avgPickingTime: number;
  inventoryAccuracy: number;
  pendingChange: number;
  shippedChange: number;
  totalPickableLocations?: number;
  occupiedLocations?: number;
  emptyLocations?: number;
  occupancyRatePercent?: number;
}

export interface HourlyData {
  hour: number;
  ordersReceived: number;
  ordersPicked: number;
  ordersPacked: number;
}

export interface ThroughputData {
  totalPicked: number;
  totalPacked: number;
  totalShipped: number;
}

export interface ActiveWave {
  id: string;
  waveNumber: string;
  waveType: string;
  assignedPickerName?: string;
  totalItems: number;
  pickedItems: number;
  progress: number;
  startedAt?: string;
}

export interface ActivityFeedItem {
  timestamp: string;
  type: string;
  description: string;
  personnelName: string;
}

export interface OverviewReport {
  totalOrders: number;
  fulfilledOrders: number;
  cancelledOrders: number;
  pendingOrders: number;
  fulfillmentRate: number;
  cancellationRate: number;
  avgFulfillmentHours: number;
  dailyData: DailyCount[];
}

export interface DailyCount {
  date: string;
  received: number;
  fulfilled: number;
  cancelled: number;
}

export interface PickerPerformance {
  pickerName: string;
  waveCount: number;
  itemCount: number;
  avgDuration: number;
  accuracy: number;
  efficiency: number;
}

/** @deprecated Artık kullanılmıyor — yeni API PackingOrderReport döndürüyor */
export interface PackerPerformance {
  packerName: string;
  packageCount: number;
  avgDuration: number;
  errorCount: number;
  efficiency: number;
}

export interface PackingOrderReport {
  date: string;
  orderNumber: string;
  orderType?: string;
  itemCount: number;
  shippingMethod?: string;
  status: string;
}

export interface ReceivingReportItem {
  date: string;
  sessionNumber: string;
  productTypes: number;
  totalItems: number;
  placedItems: number;
  placedPercent: number;
}

export interface CountingReportItem {
  taskNumber: string;
  date: string;
  totalLocations: number;
  discrepancyCount: number;
  accuracyPercent: number;
}

export interface ShortPickReportItem {
  date: string;
  productName: string;
  locationCode: string;
  pickerName: string;
  description?: string;
}

interface DateRange {
  dateFrom?: string;
  dateTo?: string;
}

const dashboardService = {
  getSummary: async (warehouseId: string): Promise<DashboardSummary> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_SUMMARY(warehouseId));
    return resp.data?.data;
  },

  getHourly: async (warehouseId: string): Promise<HourlyData[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_HOURLY(warehouseId));
    return resp.data?.data ?? [];
  },

  getThroughput: async (warehouseId: string): Promise<ThroughputData> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_THROUGHPUT(warehouseId));
    return resp.data?.data ?? { totalPicked: 0, totalPacked: 0, totalShipped: 0 };
  },

  getActiveWaves: async (warehouseId: string): Promise<ActiveWave[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_ACTIVE_WAVES(warehouseId));
    return resp.data?.data ?? [];
  },

  getActivityFeed: async (warehouseId: string): Promise<ActivityFeedItem[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_DASHBOARD_FEED(warehouseId));
    return resp.data?.data ?? [];
  },

  getOverviewReport: async (warehouseId: string, range?: DateRange): Promise<OverviewReport> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_OVERVIEW(warehouseId), { params: range });
    return resp.data?.data;
  },

  getPickingReport: async (warehouseId: string, range?: DateRange): Promise<PickerPerformance[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_PICKING(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },

  getPackingReport: async (warehouseId: string, range?: DateRange): Promise<PackingOrderReport[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_PACKING(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },

  getReceivingReport: async (warehouseId: string, range?: DateRange): Promise<ReceivingReportItem[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_RECEIVING(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },

  getCountingReport: async (warehouseId: string, range?: DateRange): Promise<CountingReportItem[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_COUNTING(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },

  getShortPicksReport: async (warehouseId: string, range?: DateRange): Promise<ShortPickReportItem[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_REPORT_SHORT_PICKS(warehouseId), { params: range });
    return resp.data?.data ?? [];
  },
};

export default dashboardService;
